/**
 * ADR-70 cut 6a (hitlist sections 15 and 16): the ring console's bid history
 * and pace.
 *
 * The console folds the floor wire into the ring's activity as events arrive
 * (auction-core ring-activity.ts), the same fold the API runs over the
 * ring's log. It reads the API when the first snapshot arrives and again
 * whenever it has missed events: a reconnect, a gap or a resync skips them,
 * and the snapshot that follows is ahead of what was folded. While a read is
 * out, live events are folded and kept, then folded again over the answer
 * (the fold ignores what it already has), so nothing that arrived meanwhile
 * is lost.
 *
 * Pure, so the rules are testable without a browser or a ring. The caller
 * draws every string here as a text node: bidder names and reasons are
 * untrusted.
 */

import { emptyRingActivity, foldRingActivity, ringPace, type BidRow, type BidSource, type DomainEvent, type RingActivity, type RingPace } from '@hipnumber/auction-core';

/** NONE: not read yet. LOADING: the first read is out. SEEDED: the ring's
 *  record was read. UNREAD: it could not be, so the screen holds only what
 *  arrived since it opened. */
export type HistoryStatus = 'NONE' | 'LOADING' | 'SEEDED' | 'UNREAD';

/** The least time between two reads of the ring's record, and after one
 *  that failed. A snapshot follows every commit, so a busy ring would
 *  otherwise ask on every bid while its record was out of reach. */
export const REREAD_MIN_MS = 5_000;
export const REREAD_AFTER_FAILURE_MS = 30_000;

export interface ActivityReply {
  readonly ringId: string;
  readonly read: boolean;
  readonly activity: RingActivity | null;
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const SOURCES: readonly string[] = ['FLOOR', 'ONLINE', 'PROXY'];

function looksLikeBid(v: unknown): boolean {
  return isObj(v) && isNum(v['seq']) && isNum(v['at']) && isNum(v['amount']) && typeof v['source'] === 'string' && SOURCES.includes(v['source']);
}

function looksLikeActivity(v: unknown): v is RingActivity {
  if (!isObj(v) || !isObj(v['lots']) || !Array.isArray(v['samples']) || !isNum(v['lastSeq'])) return false;
  if (v['currentLotId'] !== null && typeof v['currentLotId'] !== 'string') return false;
  for (const l of Object.values(v['lots'])) {
    if (!isObj(l) || typeof l['lotId'] !== 'string' || !isNum(l['bidCount'])) return false;
    if (l['bids'] !== null && !(Array.isArray(l['bids']) && l['bids'].every(looksLikeBid))) return false;
  }
  return v['samples'].every((s) => isObj(s) && typeof s['lotId'] === 'string' && isNum(s['ms']) && isNum(s['lots']));
}

/**
 * The API's answer (GET /sales/:saleId/ring-activity), or null when it is
 * not one. A record that could not be read comes back as `read: false`.
 */
export function parseActivityReply(body: unknown): ActivityReply | null {
  if (!isObj(body) || typeof body['ringId'] !== 'string' || typeof body['read'] !== 'boolean') return null;
  if (!body['read'] || body['activity'] === null) return { ringId: body['ringId'], read: false, activity: null };
  const a = body['activity'];
  if (!looksLikeActivity(a)) return null;
  return { ringId: body['ringId'], read: true, activity: { ...a, expectAccepted: a.expectAccepted ?? null, expectAward: a.expectAward ?? null } };
}

/**
 * The read carries bids for a few lots only. A lot this screen watched
 * whole keeps the bids it already has, when the read agrees it is the same
 * offering with the same count.
 */
function keepWatchedBids(had: RingActivity, next: RingActivity): void {
  for (const [id, o] of Object.entries(had.lots)) {
    const n = next.lots[id];
    if (n && n.bids === null && o.bids !== null && o.enteredAt === n.enteredAt && o.bidCount === n.bidCount) n.bids = o.bids;
  }
}

/** Fold one event; true when it skipped past an event never folded. */
function foldNoting(a: RingActivity, ev: DomainEvent): boolean {
  const skipped = a.lastSeq > 0 && ev.seq > a.lastSeq + 1;
  foldRingActivity(a, ev);
  return skipped;
}

export class RingHistory {
  activity: RingActivity = emptyRingActivity();
  status: HistoryStatus = 'NONE';
  /** Something was missed since the last read: read again when allowed. */
  private behind = true;
  private reading = false;
  private seqAtRead = 0;
  private kept: DomainEvent[] = [];
  private lastReadAt: number | null = null;
  private lastReadFailed = false;
  private readonly askedAt = new Map<string, number>();

  get busy(): boolean {
    return this.reading;
  }

  /** One event from the floor wire. Anything without a seq and a time is ignored. */
  fold(ev: Record<string, unknown>): void {
    if (!isNum(ev['seq']) || !isNum(ev['serverTs']) || typeof ev['type'] !== 'string') return;
    const e = ev as unknown as DomainEvent;
    if (this.reading) this.kept.push(e);
    if (foldNoting(this.activity, e) && this.status !== 'NONE') this.behind = true;
  }

  /** A snapshot ahead of what was folded means events were missed. */
  noteSnapshot(seq: unknown): void {
    if (isNum(seq) && seq > this.activity.lastSeq) this.behind = true;
  }

  /** Whether to read the ring's record now. */
  shouldRead(now: number): boolean {
    if (this.reading || !this.behind) return false;
    if (this.lastReadAt === null) return true;
    return now - this.lastReadAt >= (this.lastReadFailed ? REREAD_AFTER_FAILURE_MS : REREAD_MIN_MS);
  }

  /** Bids for a lot this screen does not carry yet (an earlier lot, picked
   *  from the results), nothing else being read, and not just asked for. */
  wantsBidsFor(lotId: string | null, now: number): boolean {
    if (this.reading || lotId === null || this.activity.lots[lotId]?.bids !== null) return false;
    const asked = this.askedAt.get(lotId);
    return asked === undefined || now - asked >= REREAD_AFTER_FAILURE_MS;
  }

  /** A read starts; `lotIds` are the lots it asks bids for. */
  begin(now: number, lotIds: readonly string[] = []): void {
    this.reading = true;
    this.kept = [];
    this.seqAtRead = this.activity.lastSeq;
    this.lastReadAt = now;
    for (const id of lotIds) this.askedAt.set(id, now);
    if (this.status === 'NONE') this.status = 'LOADING';
  }

  /** The read's answer; null when the request failed or was not understood. */
  settle(reply: ActivityReply | null): void {
    this.reading = false;
    const kept = this.kept;
    this.kept = [];
    const next = reply?.read ? reply.activity : null;
    // A record behind what this screen already saw is no better than none.
    if (!next || next.lastSeq < this.seqAtRead) {
      this.lastReadFailed = true;
      if (this.status !== 'SEEDED') this.status = 'UNREAD';
      return;
    }
    this.lastReadFailed = false;
    keepWatchedBids(this.activity, next);
    let skipped = false;
    for (const e of kept) if (foldNoting(next, e)) skipped = true;
    this.activity = next;
    this.status = 'SEEDED';
    this.behind = skipped;
  }
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/** What the ring calls the source, in a clerk's words. The enum decides;
 *  the words never do (hitlist section 16). */
export function bidSourceWord(source: BidSource): string {
  return source === 'FLOOR' ? 'Floor' : source === 'PROXY' ? 'Max bid' : 'Online';
}

const ORIGIN_WORDS: Readonly<Record<string, string>> = {
  WEB_DETAIL: 'Lot page',
  WEB_CATALOG: 'Catalog',
  WEB_LIVE: 'Live page',
  MOBILE_WEB: 'Mobile site',
  MOBILE_APP: 'App',
  OPERATOR: 'Office',
  RING_CLERK: 'Clerk console',
  ONSITE: 'On site',
  PHONE: 'Telephone',
  ABSENTEE: 'Absentee',
  MAX_BID: 'Max bid',
  API: 'API',
  AI_AGENT: 'AI agent',
  IMPORT: 'Import',
};

const DEVICE_WORDS: Readonly<Record<string, string>> = { DESKTOP: 'Desktop', TABLET: 'Tablet', MOBILE: 'Mobile' };

const wordFor = (words: Readonly<Record<string, string>>, v: unknown): string | null =>
  typeof v === 'string' && Object.prototype.hasOwnProperty.call(words, v) ? words[v]! : null;

/**
 * ADR-70 cut 6b (hitlist 8.2, 16): where a bid came from, e.g. "Live page ·
 * Mobile", or null when the record does not say. A max bid's own row already
 * says "Max bid" in its source; an UNKNOWN device says nothing.
 */
export function bidViaWords(row: Pick<BidRow, 'source' | 'origin' | 'deviceClass'>): string | null {
  const origin = row.source === 'PROXY' && row.origin === 'MAX_BID' ? null : wordFor(ORIGIN_WORDS, row.origin);
  const words = [origin, wordFor(DEVICE_WORDS, row.deviceClass)].filter((w): w is string => w !== null);
  return words.length ? words.join(' · ') : null;
}

/** The registration fields the bidder's name and paddle are read from. */
export interface BidderReg {
  readonly livePaddle: number | null;
  readonly onlinePaddle: number | null;
  readonly bidderName?: string | null;
  readonly ringBidderId?: string | null;
}

/**
 * Who bid. A floor bid is known by the paddle the clerk typed (most are
 * typed after the hammer, on the sale, not on the bid); an online bid by
 * the bidder's ring identity, matched to their registration.
 */
export function bidderWords(row: Pick<BidRow, 'source' | 'paddle' | 'bidderId' | 'bidderRef'>, regs: readonly BidderReg[]): string {
  if (row.source === 'FLOOR') {
    if (!row.paddle) return 'No number';
    const r = regs.find((g) => g.livePaddle !== null && String(g.livePaddle) === row.paddle);
    return r?.bidderName ? `Paddle ${row.paddle} · ${r.bidderName}` : `Paddle ${row.paddle}`;
  }
  const r = regs.find((g) => !!g.ringBidderId && g.ringBidderId === row.bidderId);
  if (!r) return row.bidderRef;
  const pad = r.onlinePaddle ?? r.livePaddle;
  const words = [pad !== null ? `Paddle ${pad}` : null, r.bidderName ?? null].filter((w): w is string => !!w);
  return words.length ? words.join(' · ') : row.bidderRef;
}

export interface BidMark {
  readonly text: string;
  /** UNDONE: the bid no longer stands. */
  readonly kind: 'UNDONE' | 'NOTE';
}

/** The markers a row carries, in the order they happened. */
export function bidMarks(row: Pick<BidRow, 'operatorAccepted' | 'equalBidAward' | 'undone'>): BidMark[] {
  const marks: BidMark[] = [];
  if (row.operatorAccepted) marks.push({ kind: 'NOTE', text: 'Accepted by the clerk' });
  const award = row.equalBidAward;
  if (award) {
    const side = award.to === 'ONSITE' ? 'the floor' : 'online';
    marks.push({ kind: 'NOTE', text: `Equal bid to ${side}, ${award.cause === 'OPERATOR' ? 'by the clerk' : "by the ring's rule"}` });
  }
  const undone = row.undone;
  if (undone) {
    // ADR-70 cut 6c: "Removed" is the clerk's Remove last bid (any source);
    // "Retracted" stays the older floor-bid correction's word.
    const what = undone.kind === 'CORRECTED' ? 'Retracted'
      : undone.kind === 'RETRACTED' ? 'Removed by the clerk'
      : undone.kind === 'RESET' ? 'Bidding reset'
      : 'Undone, the online bid stands again';
    marks.push({ kind: 'UNDONE', text: undone.reason ? `${what}: ${undone.reason}` : what });
  }
  return marks;
}

/** A lot's bids, newest first; online and max bids only when asked. */
export function bidRows(activity: RingActivity, lotId: string | null, opts: { readonly onlineOnly: boolean }): BidRow[] | null {
  const bids = lotId ? activity.lots[lotId]?.bids : undefined;
  if (bids === undefined) return [];
  if (bids === null) return null;
  const rows = opts.onlineOnly ? bids.filter((b) => b.source !== 'FLOOR') : [...bids];
  return rows.reverse();
}

/** "1:42", or "1:02:03" past the hour; "-" for nothing to show. */
export function duration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return '-';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Lots an hour, to one place: "26.3". */
export function perHour(n: number | null): string {
  return n === null || !Number.isFinite(n) ? '-' : n.toFixed(1);
}

export interface PaceCells {
  readonly inRing: string;
  readonly avgPerLot: string;
  readonly lotsPerHour: string;
  readonly last10: string;
  readonly complete: string;
  readonly left: string;
  readonly estEnd: string;
}

/**
 * The pace panel's figures. `lotIds` is the run this ring sells, scratched
 * lots left out. `clock` turns a time into the screen's words for it.
 */
export function paceCells(activity: RingActivity, opts: { readonly now: number; readonly lotIds: readonly string[]; readonly clock: (ms: number) => string }): PaceCells {
  const p: RingPace = ringPace(activity, { now: opts.now, lotIds: opts.lotIds });
  return {
    inRing: duration(p.timeInRingMs),
    avgPerLot: duration(p.avgPerLotMs),
    lotsPerHour: perHour(p.lotsPerHour),
    last10: perHour(p.last10LotsPerHour),
    complete: `${p.completed} / ${p.total}`,
    left: p.total === 0 ? '-' : `${p.remaining} left`,
    estEnd: p.estimatedEndAt === null ? (p.total > 0 && p.remaining === 0 ? 'Done' : '-') : opts.clock(p.estimatedEndAt),
  };
}

/** The one line the history says about where its rows came from, or null. */
export function historyNote(status: HistoryStatus): string | null {
  if (status === 'NONE' || status === 'LOADING') return "Reading the ring's record…";
  if (status === 'UNREAD') return "Could not read the ring's record. Showing what happened since this screen opened.";
  return null;
}
